import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HeaderPortal, useSetHeaderRecordName } from "../../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import Skeleton from "../../../components/ui-components/Skeleton";
import Badge from "../../../components/ui-components/Badge";
import { toast } from "../../../components/ui-components/Toast";
import { getBudgetPolicy, createBudgetPolicy, updateBudgetPolicy } from "../api";
import { createDefaultPolicy, createDefaultStep, serializePolicy, parsePolicy } from "./policyBuilderState";
import StepListPanel from "./components/StepListPanel";
import StepEditorPanel from "./components/StepEditorPanel";

const APPLIES_TO_OPTIONS = [
  { value: "general", label: "General" },
  { value: "ai_use_case", label: "AI use case" },
];

const PolicyEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const backToList = () => navigate("/budget-enforcement/policies");

  const { data: apiPolicy, isLoading, error } = useQuery(
    ["budget-policy", id],
    () => getBudgetPolicy(id),
    { enabled: isEdit }
  );

  const [policy, setPolicy] = useState(isEdit ? null : createDefaultPolicy());
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      setPolicy(createDefaultPolicy());
      setSelectedStepId(null);
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit && apiPolicy) {
      const parsed = parsePolicy(apiPolicy);
      setPolicy(parsed);
      setSelectedStepId(parsed.steps[0]?.id || null);
    }
  }, [isEdit, apiPolicy]);

  const setField = (field, value) => setPolicy((p) => ({ ...p, [field]: value }));

  const handleAddStep = () => {
    const step = createDefaultStep();
    setPolicy((p) => ({ ...p, steps: [...(p.steps || []), step] }));
    setSelectedStepId(step.id);
  };
  const handleStepsChange = (newSteps) => setPolicy((p) => ({ ...p, steps: newSteps }));
  const handleStepChange = (updatedStep) =>
    setPolicy((p) => ({ ...p, steps: p.steps.map((s) => (s.id === updatedStep.id ? updatedStep : s)) }));

  const selectedStep = policy ? (policy.steps || []).find((s) => s.id === selectedStepId) || null : null;

  const handleSave = async () => {
    if (!policy) return;
    setIsSaving(true);
    try {
      if (isEdit) {
        await updateBudgetPolicy(id, serializePolicy(policy));
        toast.success("Policy updated");
      } else {
        await createBudgetPolicy(serializePolicy(policy));
        toast.success("Policy created");
      }
      backToList();
    } catch (e) {
      toast.error("Failed to save policy");
    } finally {
      setIsSaving(false);
    }
  };

  const policyName = isEdit ? policy?.name || null : null;
  const headerTitle = policyName || (isEdit ? "Budget policy" : "New budget policy");

  useSetHeaderRecordName(policyName);

  // -- Loading / error states (same shell as /control-policies/approval-policies/:id) --
  if (isEdit && isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={400} />
      </div>
    );
  }
  if (isEdit && error) {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <div className="sticky top-0 z-40 border-b border-border flex items-center h-[64px] pl-8 pr-4">
          <span className="text-lg font-semibold text-text">Budget policy</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-danger mb-2">Error loading policy</div>
            <button type="button" className={secondaryButtonCls} onClick={backToList}>
              Back to list
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!policy) return null;

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Action buttons live in the global top bar (next to the tab switcher), like /approvals/:id. */}
      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          <button type="button" className={secondaryButtonCls} onClick={backToList}>
            Cancel
          </button>
          <button type="button" className={primaryButtonCls} onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </HeaderPortal>

      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {headerTitle}
          </div>
          {policy.is_default && <Badge status="info" label="Default" />}
        </div>
      </HeaderPortal>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Metadata card */}
        <div className="bg-card border border-card-bd rounded-card p-5">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[240px] flex-1 max-w-[360px]">
              <label htmlFor="policy-name" className="block text-sm font-medium text-text-2 mb-1">
                Policy name
              </label>
              <input
                id="policy-name"
                type="text"
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                placeholder="e.g. AI spend guardrail"
                value={policy.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>
            <div className="min-w-[200px] flex-1">
              <label htmlFor="policy-description" className="block text-sm font-medium text-text-2 mb-1">
                Description
              </label>
              <input
                id="policy-description"
                type="text"
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                placeholder="Optional description"
                value={policy.description || ""}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>
            <div className="min-w-[160px]">
              <label htmlFor="policy-applies-to" className="block text-sm font-medium text-text-2 mb-1">
                Applies to
              </label>
              <select
                id="policy-applies-to"
                className="w-full p-2 border border-border rounded-ctrl text-sm bg-card text-text"
                value={policy.applies_to}
                onChange={(e) => setField("applies_to", e.target.value)}
              >
                {APPLIES_TO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
              <input
                type="checkbox"
                checked={!!policy.is_default}
                onChange={(e) => setField("is_default", e.target.checked)}
              />
              Use as organization default policy
            </label>
          </div>
        </div>

        {/* Thresholds card */}
        <div className="bg-card border border-card-bd rounded-card p-5">
          <h3 className="text-sm font-semibold text-text mb-4">Thresholds</h3>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[120px]">
              <label htmlFor="policy-soft-pct" className="block text-sm font-medium text-text-2 mb-1">
                Soft threshold (%)
              </label>
              <input
                id="policy-soft-pct"
                type="number"
                min={0}
                max={200}
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                value={policy.soft_pct ?? ""}
                onChange={(e) => setField("soft_pct", e.target.value)}
              />
            </div>
            <div className="min-w-[120px]">
              <label htmlFor="policy-hard-pct" className="block text-sm font-medium text-text-2 mb-1">
                Hard threshold (%)
              </label>
              <input
                id="policy-hard-pct"
                type="number"
                min={0}
                max={200}
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                value={policy.hard_pct ?? ""}
                onChange={(e) => setField("hard_pct", e.target.value)}
              />
            </div>
            <div className="min-w-[140px]">
              <label htmlFor="policy-breach-eta" className="block text-sm font-medium text-text-2 mb-1">
                Breach ETA (days)
              </label>
              <input
                id="policy-breach-eta"
                type="number"
                min={1}
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                value={policy.breach_eta_days ?? ""}
                onChange={(e) => setField("breach_eta_days", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Spike detection card */}
        <div className="bg-card border border-card-bd rounded-card p-5">
          <h3 className="text-sm font-semibold text-text mb-4">Spike detection</h3>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
              <input
                type="checkbox"
                checked={!!policy.anomaly_enabled}
                onChange={(e) => setField("anomaly_enabled", e.target.checked)}
              />
              Enable anomaly / spike detection
            </label>
            {policy.anomaly_enabled && (
              <div className="flex flex-wrap gap-4 items-end pl-6">
                <div className="min-w-[160px]">
                  <label htmlFor="policy-spike-mult" className="block text-sm font-medium text-text-2 mb-1">
                    Spike multiplier
                  </label>
                  <input
                    id="policy-spike-mult"
                    type="number"
                    min={1}
                    step={0.1}
                    className="w-full p-2 border border-border rounded-ctrl text-sm"
                    placeholder="e.g. 2.5"
                    value={policy.spike_multiplier ?? ""}
                    onChange={(e) => setField("spike_multiplier", e.target.value === "" ? null : e.target.value)}
                  />
                </div>
                <div className="min-w-[160px]">
                  <label htmlFor="policy-spike-baseline" className="block text-sm font-medium text-text-2 mb-1">
                    Spike baseline (days)
                  </label>
                  <input
                    id="policy-spike-baseline"
                    type="number"
                    min={1}
                    className="w-full p-2 border border-border rounded-ctrl text-sm"
                    placeholder="e.g. 30"
                    value={policy.spike_baseline_days ?? ""}
                    onChange={(e) => setField("spike_baseline_days", e.target.value === "" ? null : e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Escalation steps: list + editor (mirrors the approval-policy tier builder) */}
        <div className="grid gap-6 md:grid-cols-[360px_minmax(0,1fr)]">
          <StepListPanel
            steps={policy.steps || []}
            selectedStepId={selectedStepId}
            onSelectStep={setSelectedStepId}
            onStepsChange={handleStepsChange}
            onAddStep={handleAddStep}
          />
          <StepEditorPanel step={selectedStep} onStepChange={handleStepChange} />
        </div>
      </div>
    </div>
  );
};

export default PolicyEditorPage;
